import React from 'react';

/**
 * A página que explica o Open Finance, dentro do app.
 *
 * ── Para quem é ───────────────────────────────────────────────────────────
 *
 * Para quem abriu a função e ainda não confia. Ligar o banco a um app é a
 * decisão mais sensível que o FinTracking pede a alguém, e quem decide sem
 * entender desiste no meio ou se arrepende depois. Então ela vem ANTES do
 * botão de conectar, e continua a um toque de distância depois.
 *
 * ── O que ela precisa dizer, na ordem em que a dúvida aparece ────────────
 *
 *   1. o que é, e quem garante
 *   2. o que o app consegue fazer — e o que NÃO consegue
 *   3. como conectar
 *   4. como os lançamentos entram
 *   5. o que fica de fora, para ninguém achar que sumiu
 *   6. quem vê os dados, e como sair
 *
 * ── Só afirma o que o código faz ──────────────────────────────────────────
 *
 * Cada frase daqui tem uma regra atrás: o que é descartado está em
 * `banco/normalizar.ts`, o que nunca entra sozinho em `banco/repo.ts`, os
 * terceiros em `privacidade.html`. Texto de explicação que promete mais do
 * que o app faz é a forma mais barata de perder a confiança que ele pede.
 *
 * Texto fixo, sem dado dela e sem chamada: abre na hora e funciona offline.
 */

interface Props {
  /** Volta para a tela da função. */
  onVoltar: () => void;
}

const ComoFuncionaOpenFinance: React.FC<Props> = ({ onVoltar }) => (
  <div className="bco-ajuda">
    <button type="button" className="bco-link bco-voltar" onClick={onVoltar}>← voltar</button>

    <h3>O que é</h3>
    <p>
      Open Finance é o sistema do Banco Central que deixa você compartilhar os dados do seu
      banco com outro serviço, se quiser. É o mesmo que os bancos usam quando mostram, dentro
      do app deles, o saldo de uma conta sua em outro banco.
    </p>
    <p>
      Quem autoriza é você, <strong>no site ou no app do seu banco</strong>. O FinTracking nunca
      vê a sua senha.
    </p>

    <h3>O que o FinTracking consegue fazer</h3>
    <ul>
      <li>Ler os movimentos da sua conta e do seu cartão.</li>
      <li>Ler os seus contratos de empréstimo, quando você pede.</li>
    </ul>
    <p className="bco-ajuda-destaque">
      É leitura apenas. Daqui <strong>não sai Pix, pagamento nem transferência</strong>: a
      conexão não tem como mover dinheiro.
    </p>

    <h3>Como conectar</h3>
    <ol>
      <li>
        Conecte o seu banco na sua conta do{' '}
        <a href="https://app.mcp.ai" target="_blank" rel="noopener noreferrer">Banco MCP</a>, o
        serviço que faz a ponte. Ele te leva ao site do seu banco para autorizar.
      </li>
      <li>Volte aqui e toque em <strong>Buscar meus bancos</strong>. Ele lê os últimos 60 dias.</li>
      <li>
        Diga uma vez <strong>que método de pagamento</strong> é cada conta do banco. Depois disso,
        nenhum lançamento pede método.
      </li>
    </ol>

    <h3>Como os lançamentos entram</h3>
    <p>
      O app descobre a categoria sozinho: pelo que você já ensinou, por como você categorizou
      aquele lugar antes, ou pela categoria que o banco informa. Quando não sabe, ele pergunta
      uma vez e lembra para os próximos.
    </p>
    <p>Você escolhe o jeito, por banco:</p>
    <ul>
      <li><strong>Conferir antes:</strong> os novos ficam esperando e você lança um a um.</li>
      <li>
        <strong>Lançar sozinho:</strong> o que chega já vira lançamento. Tudo que entra assim
        fica numa lista para você conferir, trocar a categoria ou desfazer.
      </li>
    </ul>
    <p className="bco-ajuda-destaque">
      Uma coisa nunca entra sozinha: o movimento que <strong>parece ser um lançamento que você
      já fez</strong>, com outro nome. Aí o app pergunta, para o seu aluguel não contar duas
      vezes.
    </p>

    <h3>O que não aparece, de propósito</h3>
    <ul>
      <li>Aplicação automática e rendimentos da conta.</li>
      <li>Transferência entre contas suas.</li>
      <li>Pagamento da fatura: as compras do cartão já contam uma a uma.</li>
      <li>Dinheiro de empréstimo caindo na conta: ele não é receita.</li>
      <li>Pagamento que foi devolvido ou estornado. A tela avisa qual foi.</li>
      <li>
        Movimento ainda pendente na conta corrente. Ele aparece quando efetivar. No cartão a
        compra pendente aparece, marcada como tal.
      </li>
    </ul>

    {/*
      O que o BANCO não entrega — diferente da lista de cima, que é escolha do
      app. Ela descobriu o primeiro item conectando: o cartão adicional que usa
      não veio, e nada na tela tinha dito que não viria (06/10). Limite que só
      se descobre depois de conectar é limite mal avisado.
    */}
    <h3>O que o banco não entrega</h3>
    <ul>
      <li>
        <strong>Cartão adicional de outra pessoa.</strong> A autorização que você dá vale para
        as contas e os cartões em que você é titular. Se você usa um adicional de alguém, as
        compras dele pertencem à fatura dessa pessoa e não aparecem aqui. Você continua
        lançando à mão.
      </li>
      <li>
        <strong>Conta ou cartão que não está no seu nome</strong>, mesmo que você movimente.
      </li>
      <li>
        <strong>Compra parcelada muito antiga.</strong> O banco costuma entregar cerca de um ano
        de histórico do cartão. Parcelas de uma compra anterior a isso podem não aparecer.
      </li>
    </ul>

    <h3>Quem vê os seus dados</h3>
    <p>
      A conexão é feita pelo Banco MCP e pela Pluggy, que guardam a autorização (não a senha) e
      repassam os movimentos. No FinTracking eles ficam guardados na sua conta, e só você os vê.
      Está tudo na{' '}
      <a href="/privacidade" target="_blank" rel="noopener noreferrer">política de privacidade</a>.
    </p>

    <h3>Como sair</h3>
    <p>
      Toque em <strong>desconectar</strong>, ao lado do nome do banco. A autorização é revogada e
      o que estava esperando some. Os lançamentos que já entraram continuam: são seus.
    </p>
    <p>
      A autorização também vence sozinha em até 12 meses, e às vezes o banco pede um novo login
      antes disso. Quando acontecer, a tela avisa.
    </p>

    <button type="button" className="bco-btn principal" onClick={onVoltar}>Entendi</button>
  </div>
);

export default ComoFuncionaOpenFinance;
